import type { FilterDropdownProps } from 'antd/es/table/interface'
import type { Dayjs } from 'dayjs'

import { Button, DatePicker, Form, Space } from 'antd'
import dayjs from 'dayjs'
import { useCallback } from 'react'

export interface TableDateFilterProps extends FilterDropdownProps {
  showTime?: boolean
}

export function TableDateFilter(props: TableDateFilterProps): JSX.Element {
  const { setSelectedKeys, confirm, showTime } = props
  const fromRaw = (props.selectedKeys[0] ?? undefined) as string | undefined
  const toRaw = (props.selectedKeys[1] ?? undefined) as string | undefined

  const from = fromRaw ? dayjs(fromRaw) : undefined
  const to = toRaw ? dayjs(toRaw) : undefined

  const setFrom = useCallback(
    (input: Dayjs | null) => {
      const value = input
        ? showTime
          ? input.toISOString()
          : input.startOf('day').format('YYYY-MM-DD')
        : undefined

      setSelectedKeys([value as React.Key, toRaw as React.Key])
    },
    [setSelectedKeys, toRaw, showTime]
  )

  const setTo = useCallback(
    (input: Dayjs | null) => {
      const value = input
        ? showTime
          ? input.toISOString()
          : input.endOf('day').format('YYYY-MM-DD')
        : undefined

      setSelectedKeys([fromRaw as React.Key, value as React.Key])
    },
    [setSelectedKeys, fromRaw, showTime]
  )

  const applyPreset = useCallback(
    (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
      const startValue = showTime ? start.toISOString() : start.format('YYYY-MM-DD')
      const endValue = showTime ? end.toISOString() : end.format('YYYY-MM-DD')

      setSelectedKeys([startValue as React.Key, endValue as React.Key])
      confirm()
    },
    [setSelectedKeys, confirm, showTime]
  )

  return (
    <div className='flex flex-col'>
      <div className='flex'>
        <Form
          layout='horizontal'
          className='flex flex-col pt-5 pr-5 pb-3 pl-5 w-full'
          labelAlign='left'
          labelCol={{
            className: 'w-12 font-semibold'
          }}
        >
          <Form.Item label='From' colon={false} className='mb-3'>
            <DatePicker
              placeholder={dayjs()
                .subtract(1, 'week')
                .format(showTime ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD')}
              value={from}
              onChange={setFrom}
              showTime={showTime}
              allowClear
            />
          </Form.Item>

          <Form.Item label='To' colon={false} className='mb-3'>
            <DatePicker
              placeholder={dayjs()
                .subtract(1, 'day')
                .format(showTime ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD')}
              value={to}
              onChange={setTo}
              showTime={showTime}
              allowClear
            />
          </Form.Item>
        </Form>

        <ul className='flex flex-col pt-4 pr-5 pb-3 pl-2 border-l border-l-gray-100'>
          <li>
            <Button
              type='link'
              size='small'
              className='text-left !text-xs'
              onClick={() => {
                applyPreset(dayjs().startOf('day'), dayjs().endOf('day'))
              }}
            >
              Today
            </Button>
          </li>
          <li>
            <Button
              type='link'
              size='small'
              className='text-left !text-xs'
              onClick={() => {
                applyPreset(
                  dayjs().subtract(1, 'day').startOf('day'),
                  dayjs().subtract(1, 'day').endOf('day')
                )
              }}
            >
              Yesterday
            </Button>
          </li>
          <li className='pt-1.5'>
            <Button
              type='link'
              size='small'
              className='text-left !text-xs'
              onClick={() => {
                applyPreset(
                  dayjs().startOf('isoWeek').startOf('day'),
                  dayjs().endOf('isoWeek').endOf('day')
                )
              }}
            >
              This week
            </Button>
          </li>
          <li>
            <Button
              type='link'
              size='small'
              className='text-left !text-xs'
              onClick={() => {
                applyPreset(
                  dayjs().subtract(1, 'week').startOf('isoWeek').startOf('day'),
                  dayjs().subtract(1, 'week').endOf('isoWeek').endOf('day')
                )
              }}
            >
              Last week
            </Button>
          </li>
          <li className='pt-1.5'>
            <Button
              type='link'
              size='small'
              className='text-left !text-xs'
              onClick={() => {
                applyPreset(
                  dayjs().startOf('month').startOf('day'),
                  dayjs().endOf('month').endOf('day')
                )
              }}
            >
              This month
            </Button>
          </li>
          <li>
            <Button
              type='link'
              size='small'
              className='text-left !text-xs'
              onClick={() => {
                applyPreset(
                  dayjs().subtract(1, 'month').startOf('month').startOf('day'),
                  dayjs().subtract(1, 'month').endOf('month').endOf('day')
                )
              }}
            >
              Last month
            </Button>
          </li>
        </ul>
      </div>

      <div className='flex justify-end items-center p-3 border-t border-t-gray-100'>
        <Space direction='horizontal'>
          <Button
            onClick={() => {
              setSelectedKeys([])
              confirm()
            }}
            size='small'
          >
            Reset
          </Button>

          <Button
            type='primary'
            size='small'
            onClick={() => {
              confirm()
            }}
          >
            Apply
          </Button>
        </Space>
      </div>
    </div>
  )
}
